import React, { useCallback, useEffect, useState } from 'react';
import Accordion from '../Accordion';
import { PasswordRules, passwordMeetsRules } from '../SignInScreens';
import { portalApi, sessionHeaders, portalErrorMessage } from './portalApi';
import { carrierChoices, servicesForCarrier, carrierForService } from '../shippingCarriage';

// The signed-in Customer's account page in the portal (/portal/<name>/
// account, issue #256): their Customer's shipping Preferences, changing
// their password, and changing their login email (confirmed from the new
// address), each in its own Accordion, closed until opened. Preferences
// belong to the Customer, so every login on it sees the same values.
// Shipping Options show names only — prices stay on Quotes.

const INPUT_STYLE = { width: '100%', maxWidth: 360, padding: '8px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' };
const PRIMARY_BUTTON = { padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' };
const LABEL_STYLE = { display: 'block', fontSize: 12, color: '#5a6b85', marginBottom: 4 };
const CARD_STYLE = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24, marginTop: 16 };
const INTRO_STYLE = { fontSize: 13, color: '#5a6b85', margin: '0 0 14px' };
const FORM_STYLE = { display: 'flex', flexDirection: 'column', gap: 12 };

function Feedback({ error, message }) {
  if (error) return <div style={{ color: 'red', fontSize: 13 }}>{error}</div>;
  if (message) return <div style={{ color: '#2e7d32', fontSize: 13 }}>{message}</div>;
  return null;
}

function SubmitButton({ saving, disabled, label, savingLabel }) {
  const off = saving || disabled;
  return (
    <div>
      <button type="submit" disabled={off} style={{ ...PRIMARY_BUTTON, cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.6 : 1 }}>
        {saving ? savingLabel : label}
      </button>
    </div>
  );
}

const unavailable = (item) => (item.is_active ? '' : ' (no longer offered)');

const idString = (id) => (id == null ? '' : String(id));

// Carrier, then one of its Shipping Services, then that Service's Options
// (issue #281) — the same order staff pick them in.
function PreferencesCard({ data, request, onSaved }) {
  const [carrierId, setCarrierId] = useState(idString(data.carrier_id));
  const [serviceId, setServiceId] = useState(data.shipping_service_id == null ? '' : String(data.shipping_service_id));
  const [optionIds, setOptionIds] = useState(data.shipping_option_ids.map(String));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    setCarrierId(idString(data.carrier_id));
    setServiceId(data.shipping_service_id == null ? '' : String(data.shipping_service_id));
    setOptionIds(data.shipping_option_ids.map(String));
  }, [data]);

  const service = data.shipping_services.find((s) => String(s.id) === serviceId);
  const changed = carrierId !== idString(data.carrier_id)
    || serviceId !== (data.shipping_service_id == null ? '' : String(data.shipping_service_id))
    || optionIds.length !== data.shipping_option_ids.length
    || optionIds.some((id) => !data.shipping_option_ids.map(String).includes(id));

  // Picking another Carrier clears the Service and Options; picking another
  // Service clears the Options, which belong to one Service.
  const pickCarrier = (value) => {
    setCarrierId(value);
    setServiceId('');
    setOptionIds([]);
    setMessage('');
  };
  const pickService = (value) => {
    setCarrierId(carrierForService(data.shipping_services, value, carrierId));
    setServiceId(value);
    setOptionIds([]);
    setMessage('');
  };
  const toggleOption = (id) => {
    setOptionIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await request('put', '/account/preferences', {
        carrier_id: carrierId === '' ? null : Number(carrierId),
        shipping_service_id: serviceId === '' ? null : Number(serviceId),
        shipping_option_ids: optionIds.map(Number),
      });
      if (res) {
        setMessage(res.data.message);
        onSaved(res.data.data);
      }
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not save your Preferences'));
    }
    setSaving(false);
  };

  return (
    <Accordion title="Preferences" defaultOpen={false} showMenuPlaceholder={false}>
      <p style={INTRO_STYLE}>How {data.customer_name}'s orders are shipped. These apply to everyone who signs in for {data.customer_name}.</p>
      <form onSubmit={handleSubmit} style={FORM_STYLE}>
        <div>
          <label style={LABEL_STYLE} htmlFor="portal-carrier">Carrier</label>
          <select id="portal-carrier" value={carrierId} onChange={(e) => pickCarrier(e.target.value)} style={INPUT_STYLE}>
            <option value="">No preference</option>
            {carrierChoices(data.shipping_services, carrierId).map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={LABEL_STYLE} htmlFor="portal-shipping-service">Shipping Service</label>
          <select id="portal-shipping-service" value={serviceId} onChange={(e) => pickService(e.target.value)} style={INPUT_STYLE}>
            <option value="">No preference</option>
            {servicesForCarrier(data.shipping_services, carrierId, serviceId).map((s) => (
              <option key={s.id} value={String(s.id)}>{s.description}{unavailable(s)}</option>
            ))}
          </select>
        </div>
        {service && service.options.length > 0 && (
          <div>
            <span style={LABEL_STYLE}>Shipping Options</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {service.options.map((o) => (
                <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={optionIds.includes(String(o.id))} onChange={() => toggleOption(String(o.id))} />
                  {o.description}{unavailable(o)}
                </label>
              ))}
            </div>
          </div>
        )}
        <Feedback error={error} message={message} />
        <SubmitButton saving={saving} disabled={!changed} label="Save Preferences" savingLabel="Saving..." />
      </form>
    </Accordion>
  );
}

function PasswordCard({ email, request }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (next !== confirm) {
      setError('The two new passwords don\'t match.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await request('post', '/account/password', { current_password: current, new_password: next });
      if (res) {
        setMessage(res.data.message);
        setCurrent('');
        setNext('');
        setConfirm('');
      }
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not change your password'));
    }
    setSaving(false);
  };

  return (
    <Accordion title="Password" defaultOpen={false} showMenuPlaceholder={false}>
      <p style={INTRO_STYLE}>Changing your password signs out any other device using this login.</p>
      <form onSubmit={handleSubmit} style={FORM_STYLE}>
        <input type="email" autoComplete="username" value={email} readOnly hidden />
        <div>
          <label style={LABEL_STYLE} htmlFor="portal-current-password">Current password</label>
          <input id="portal-current-password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} style={INPUT_STYLE} />
        </div>
        <div>
          <label style={LABEL_STYLE} htmlFor="portal-new-password">New password</label>
          <input id="portal-new-password" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} style={INPUT_STYLE} />
          <PasswordRules password={next} />
        </div>
        <div>
          <label style={LABEL_STYLE} htmlFor="portal-confirm-password">Confirm new password</label>
          <input id="portal-confirm-password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={INPUT_STYLE} />
        </div>
        <Feedback error={error} message={message} />
        <SubmitButton saving={saving} disabled={!current || !passwordMeetsRules(next) || !confirm} label="Change password" savingLabel="Saving..." />
      </form>
    </Accordion>
  );
}

function LoginEmailCard({ data, request, onSaved }) {
  const [newEmail, setNewEmail] = useState('');
  const [current, setCurrent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await request('post', '/account/email', { current_password: current, new_email: newEmail });
      if (res) {
        setMessage(res.data.message);
        setNewEmail('');
        setCurrent('');
        onSaved(res.data.data);
      }
    } catch (err) {
      setError(portalErrorMessage(err, 'Could not send the confirmation link'));
    }
    setSaving(false);
  };

  return (
    <Accordion title="Login email" defaultOpen={false} showMenuPlaceholder={false}>
      <p style={INTRO_STYLE}>
        You currently sign in with <strong style={{ color: '#1f2d3d', wordBreak: 'break-all' }}>{data.email}</strong>.
        <br />
        We'll email a confirmation link to the new address. Your login email changes once you click it.
      </p>
      {data.pending_email && !message && (
        <div style={{ background: '#fff8e1', border: '1px solid #f0d58c', borderRadius: 6, padding: '8px 10px', fontSize: 13, marginBottom: 14 }}>
          Waiting for you to confirm <strong style={{ wordBreak: 'break-all' }}>{data.pending_email}</strong> from the link we emailed there.
        </div>
      )}
      <form onSubmit={handleSubmit} style={FORM_STYLE}>
        <div>
          <label style={LABEL_STYLE} htmlFor="portal-new-email">New login email</label>
          <input id="portal-new-email" type="email" autoComplete="off" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} style={INPUT_STYLE} />
        </div>
        <div>
          <label style={LABEL_STYLE} htmlFor="portal-email-password">Current password</label>
          <input id="portal-email-password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} style={INPUT_STYLE} />
        </div>
        <Feedback error={error} message={message} />
        <SubmitButton saving={saving} disabled={!newEmail.trim() || !current} label="Send confirmation link" savingLabel="Sending..." />
      </form>
    </Accordion>
  );
}

function PortalAccountPage({ portalName, onSessionEnded }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const base = `/api/portal-auth/portals/${encodeURIComponent(portalName)}`;

  // Sends one account request; resolves to null (and signs out) when the
  // session has ended.
  const request = useCallback(async (method, path, body) => {
    try {
      return await portalApi[method](`${base}${path}`, body, { headers: sessionHeaders(portalName) });
    } catch (err) {
      if (err.response?.status === 401) {
        onSessionEnded();
        return null;
      }
      throw err;
    }
  }, [base, portalName, onSessionEnded]);

  useEffect(() => {
    let cancelled = false;
    portalApi.get(`${base}/account`, { headers: sessionHeaders(portalName) })
      .then((res) => { if (!cancelled) setData(res.data.data); })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 401) onSessionEnded();
        else setLoadError(portalErrorMessage(err, 'Could not load your account'));
      });
    return () => { cancelled = true; };
  }, [base, portalName, onSessionEnded]);

  return (
    <>
      <div style={{ ...CARD_STYLE, marginTop: 0 }}>
        <h1 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 600 }}>My account</h1>
        {data && (
          <div style={{ fontSize: 14, color: '#5a6b85' }}>
            Signed in for <strong style={{ color: '#1f2d3d' }}>{data.customer_name}</strong>
          </div>
        )}
        {loadError && <div style={{ color: 'red', fontSize: 13 }}>{loadError}</div>}
        {!data && !loadError && <div style={{ fontSize: 14, color: '#5a6b85' }}>Loading...</div>}
      </div>
      {data && (
        <div style={{ marginTop: 16 }}>
          <PreferencesCard data={data} request={request} onSaved={setData} />
          {/* A Login Switcher view of a Customer with no login (#283) has
              no password or login email. */}
          {data.email && <PasswordCard email={data.email} request={request} />}
          {data.email && <LoginEmailCard data={data} request={request} onSaved={setData} />}
        </div>
      )}
    </>
  );
}

export default PortalAccountPage;
